import { n8nClient } from "../apis/n8nWorkflowClient";
import {
  GET_N8N_WORKFLOW_QUERY,
  LIST_N8N_WORKFLOWS_QUERY,
  GET_EXECUTION_HISTORY_QUERY,
} from "../queries/n8nWorkflowQueries";
import {
  CREATE_N8N_WORKFLOW_MUTATION,
  UPDATE_N8N_WORKFLOW_MUTATION,
  DELETE_N8N_WORKFLOW_MUTATION,
  EXECUTE_N8N_WORKFLOW_MUTATION,
} from "../mutations/n8nWorkflowMutations";

// Get a single workflow
export const getN8nWorkflowApi = async (tenantId, workflowId) => {
  console.log(
    `N8n Workflow API - getN8nWorkflowApi - tenantId: ${tenantId}, workflowId: ${workflowId}`
  );

  try {
    const response = await n8nClient.query({
      query: GET_N8N_WORKFLOW_QUERY,
      variables: { tenantId, workflowId },
      fetchPolicy: "network-only",
    });

    return response.data.getN8nWorkflow;
  } catch (error) {
    console.error(`Error getting n8n workflow: ${error.message}`);
    throw error;
  }
};

// List all workflows for a tenant
export const listN8nWorkflowsApi = async (tenantId) => {
  console.log("=== listN8nWorkflowsApi Debug ===");
  console.log("Input tenantId:", tenantId);
  console.log("Input tenantId type:", typeof tenantId);
  console.log("Input tenantId value:", JSON.stringify(tenantId));

  try {
    const variables = { tenantId };
    console.log("Query variables:", variables);
    console.log("Query variables type:", typeof variables);
    console.log("Query variables value:", JSON.stringify(variables));

    const response = await n8nClient.query({
      query: LIST_N8N_WORKFLOWS_QUERY,
      variables,
      fetchPolicy: "network-only",
    });

    console.log("List workflows response:", response);

    if (!response.data) {
      console.error("No data in response");
      return [];
    }

    if (!response.data.listN8nWorkflows) {
      console.error("No listN8nWorkflows in response data");
      return [];
    }

    // Ensure we return an array even if empty
    const workflows = Array.isArray(response.data.listN8nWorkflows)
      ? response.data.listN8nWorkflows
      : [];

    console.log("Processed workflows:", workflows);
    return workflows;
  } catch (error) {
    console.error("=== listN8nWorkflowsApi Error ===");
    console.error("Error message:", error.message);
    console.error("Error stack:", error.stack);
    console.error("Full error:", error);
    // Return empty array instead of throwing to prevent UI issues
    return [];
  }
};

export const getN8nExecutionHistoryApi = async (input) => {
  console.log("=== getN8nExecutionHistoryApi Debug ===");
  console.log("Input received:", input);
  console.log("Input type:", typeof input);
  console.log("Input keys:", Object.keys(input));
  console.log("Input values:", JSON.stringify(input, null, 2));

  // Validate required fields
  if (!input || typeof input !== "object") {
    console.error("Invalid input: must be an object");
    throw new Error("Invalid input: must be an object");
  }

  const { tenantId, workflowId } = input;
  console.log("Extracted tenantId:", tenantId);
  console.log("Extracted workflowId:", workflowId);

  if (!tenantId || !workflowId) {
    console.error("Missing required fields:", { tenantId, workflowId });
    throw new Error("tenantId and workflowId are required");
  }

  try {
    // Format the input according to the GraphQL schema
    const queryInput = {
      tenantId,
      workflowId, // Using workflowId as per schema
    };

    console.log("Making GraphQL query with variables:", { input: queryInput });
    console.log("Query variables type:", typeof queryInput);
    console.log(
      "Query variables stringified:",
      JSON.stringify({ input: queryInput }, null, 2)
    );

    const response = await n8nClient.query({
      query: GET_EXECUTION_HISTORY_QUERY,
      variables: { input: queryInput },
      fetchPolicy: "network-only",
    });

    console.log("GraphQL response:", response);
    console.log("GraphQL response data:", response.data);
    console.log("GraphQL response errors:", response.errors);

    if (!response.data) {
      console.error("No data in response");
      return [];
    }

    if (!response.data.getExecutionHistory) {
      console.error("No getExecutionHistory in response data");
      return [];
    }

    return response.data.getExecutionHistory;
  } catch (error) {
    console.error("=== getN8nExecutionHistoryApi Error ===");
    console.error("Error message:", error.message);
    console.error("Error stack:", error.stack);
    console.error("Full error:", error);
    throw error; // Re-throw the error to be handled by the saga
  }
};

// Create a new workflow
export const createN8nWorkflowApi = async (input) => {
  console.log(
    `N8n Workflow API - createN8nWorkflowApi - input: ${JSON.stringify(input)}`
  );

  try {
    const response = await n8nClient.mutate({
      mutation: CREATE_N8N_WORKFLOW_MUTATION,
      variables: { input },
    });

    return response.data.createN8nWorkflow;
  } catch (error) {
    console.error(`Error creating n8n workflow: ${error.message}`);
    throw error;
  }
};

// Update an existing workflow
export const updateN8nWorkflowApi = async (input) => {
  console.log(
    `N8n Workflow API - updateN8nWorkflowApi - input: ${JSON.stringify(input)}`
  );

  try {
    const response = await n8nClient.mutate({
      mutation: UPDATE_N8N_WORKFLOW_MUTATION,
      variables: { input },
    });

    return response.data.updateN8nWorkflow;
  } catch (error) {
    console.error(`Error updating n8n workflow: ${error.message}`);
    throw error;
  }
};

// Delete a workflow
export const deleteN8nWorkflowApi = async (input) => {
  console.log(
    `N8n Workflow API - deleteN8nWorkflowApi - input: ${JSON.stringify(input)}`
  );

  try {
    const response = await n8nClient.mutate({
      mutation: DELETE_N8N_WORKFLOW_MUTATION,
      variables: { input },
    });

    return response.data.deleteN8nWorkflow;
  } catch (error) {
    console.error(`Error deleting n8n workflow: ${error.message}`);
    throw error;
  }
};

// Execute a workflow
export const executeN8nWorkflowApi = async (input) => {
  console.log(
    `N8n Workflow API - executeN8nWorkflowApi - input: ${JSON.stringify(input)}`
  );

  try {
    const response = await n8nClient.mutate({
      mutation: EXECUTE_N8N_WORKFLOW_MUTATION,
      variables: { input },
    });

    return response.data.executeN8nWorkflow;
  } catch (error) {
    console.error(`Error executing n8n workflow: ${error.message}`);
    throw error;
  }
};
